import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { MODEL_OPTIONS, type HistoryResponse } from "./bop030w020";

interface BOP030W020TemplateProps {
  detailMode: boolean;
  targetMonth: string;
  startMonth: string;
  endMonth: string;
  question: string;
  model: string;
  displayStatus: "idle" | "loading" | "answered" | "failed";
  answer: string | null;
  usedModel: string | null;
  usedInputTokens: number | null;
  usedOutputTokens: number | null;
  usedCostUsd: number | null;
  errorMessage: string | null;
  isSubmitDisabled: boolean;
  maxQuestionLength: number;
  onDetailModeChange: (value: boolean) => void;
  onTargetMonthChange: (value: string) => void;
  onStartMonthChange: (value: string) => void;
  onEndMonthChange: (value: string) => void;
  onQuestionChange: (value: string) => void;
  onModelChange: (value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onBack: () => void;
  historyMonthFrom: string;
  historyMonthTo: string;
  historyResults: HistoryResponse[];
  isHistoryLoading: boolean;
  historyErrorMessage: string | null;
  onHistoryMonthFromChange: (value: string) => void;
  onHistoryMonthToChange: (value: string) => void;
  onHistorySearch: (e: React.FormEvent<HTMLFormElement>) => void;
}

const BOP030W020Template = ({
  detailMode,
  targetMonth,
  startMonth,
  endMonth,
  question,
  model,
  displayStatus,
  answer,
  usedModel,
  usedInputTokens,
  usedOutputTokens,
  usedCostUsd,
  errorMessage,
  isSubmitDisabled,
  maxQuestionLength,
  onDetailModeChange,
  onTargetMonthChange,
  onStartMonthChange,
  onEndMonthChange,
  onQuestionChange,
  onModelChange,
  onSubmit,
  onBack,
  historyMonthFrom,
  historyMonthTo,
  historyResults,
  isHistoryLoading,
  historyErrorMessage,
  onHistoryMonthFromChange,
  onHistoryMonthToChange,
  onHistorySearch,
}: BOP030W020TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();

  const handleDateInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker?.();
  };

  const formatHistoryMonth = (yyyymm: string) =>
    `${yyyymm.slice(0, 4)}年${yyyymm.slice(4, 6)}月`;

  const formatHistoryPeriod = (row: HistoryResponse) => {
    if (!row.endMonth || row.endMonth === row.startMonth) {
      return formatHistoryMonth(row.startMonth);
    }
    return `${formatHistoryMonth(row.startMonth)} 〜 ${formatHistoryMonth(row.endMonth)}`;
  };

  const formatHistoryDate = (registerDt: string) =>
    registerDt ? registerDt.replace("T", " ").slice(0, 16) : "-";

  const formatCostUsd = (costUsd: number) => `$${costUsd.toFixed(6)}`;

  const formatTokens = (inputTokens: number, outputTokens: number) =>
    `入力${inputTokens.toLocaleString()} / 出力${outputTokens.toLocaleString()} tokens`;

  return (
    <div
      className={`bop030w020-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      <h1 className="screen-title">AI家計相談</h1>

      <div className="bop030w020-content">
        <form id="askForm" onSubmit={onSubmit}>
          <div className="form-row">
            <label className="form-label">詳細データ分析</label>
            <label className="toggle-switch" aria-label="詳細データ分析トグル">
              <input
                id="bop030w020-detailMode"
                type="checkbox"
                checked={detailMode}
                aria-describedby="bop030w020-detailModeHint"
                onChange={(e) => onDetailModeChange(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
            <span id="bop030w020-detailModeHint" className="detail-mode-hint">
              ONにすると、集計値ではなく対象月の取引明細（メモ含む）をそのままAIに送信して分析します。API利用コストが増加するため対象は単月に限られます。
            </span>
          </div>
          {detailMode ? (
            <div className="form-row">
              <label className="form-label required-label" htmlFor="bop030w020-targetMonth">
                対象月
              </label>
              <input
                id="bop030w020-targetMonth"
                type="month"
                required
                aria-required="true"
                value={targetMonth}
                onChange={(e) => onTargetMonthChange(e.target.value)}
                onClick={handleDateInputClick}
              />
            </div>
          ) : (
            <>
              <div className="form-row">
                <label className="form-label required-label" htmlFor="bop030w020-startMonth">
                  開始月
                </label>
                <input
                  id="bop030w020-startMonth"
                  type="month"
                  required
                  aria-required="true"
                  value={startMonth}
                  onChange={(e) => onStartMonthChange(e.target.value)}
                  onClick={handleDateInputClick}
                />
              </div>
              <div className="form-row">
                <label className="form-label required-label" htmlFor="bop030w020-endMonth">
                  終了月
                </label>
                <input
                  id="bop030w020-endMonth"
                  type="month"
                  required
                  aria-required="true"
                  value={endMonth}
                  onChange={(e) => onEndMonthChange(e.target.value)}
                  onClick={handleDateInputClick}
                />
              </div>
            </>
          )}
          <div className="form-row">
            <label className="form-label" htmlFor="bop030w020-model">
              モデル
            </label>
            <select
              id="bop030w020-model"
              value={model}
              onChange={(e) => onModelChange(e.target.value)}
            >
              {MODEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row question-row">
            <label className="form-label required-label" htmlFor="bop030w020-question">
              質問
            </label>
            <textarea
              id="bop030w020-question"
              className="question-textarea"
              required
              aria-required="true"
              value={question}
              maxLength={maxQuestionLength}
              placeholder="例: 今月は先月と比べて何にお金を使いすぎていますか？"
              onChange={(e) => onQuestionChange(e.target.value)}
            />
            <div className="question-counter">
              {question.length} / {maxQuestionLength}
            </div>
          </div>
        </form>
      </div>

      <div className="action-panel">
        <div className="action-group action-group-left">
          <button type="button" className="clear-button" onClick={onBack}>
            戻る
          </button>
        </div>
        <div className="action-group action-group-right">
          <button
            form="askForm"
            type="submit"
            className="search-button"
            disabled={isSubmitDisabled}
          >
            送信
          </button>
        </div>
      </div>

      <div
        className="answer-card"
        role={displayStatus === "failed" ? "alert" : "status"}
        aria-live={displayStatus === "failed" ? "assertive" : "polite"}
      >
        {displayStatus === "idle" && (
          <div className="answer-placeholder">
            対象期間と質問を入力して送信してください。
          </div>
        )}
        {displayStatus === "loading" && (
          <Spinner label="AIが分析中です…" />
        )}
        {displayStatus === "answered" && (
          <>
            <div className="answer-text">{answer}</div>
            {(usedModel || usedCostUsd != null) && (
              <div className="answer-model-caption">
                {[
                  usedModel ? `使用モデル: ${usedModel}` : null,
                  usedInputTokens != null && usedOutputTokens != null
                    ? formatTokens(usedInputTokens, usedOutputTokens)
                    : null,
                  usedCostUsd != null ? `API利用料金: ${formatCostUsd(usedCostUsd)}` : null,
                ]
                  .filter(Boolean)
                  .join(" / ")}
              </div>
            )}
          </>
        )}
        {displayStatus === "failed" && (
          <div className="error-box">{errorMessage}</div>
        )}
      </div>

      <div className="history-section">
        <h2 className="history-title">AI家計相談履歴</h2>

        <div className="search-layout">
          <details className="search-details" open>
            <summary>検索条件</summary>
            <form id="historySearchForm" onSubmit={onHistorySearch}>
              <div className="form-row">
                <label className="form-label" htmlFor="bop030w020-historyMonthFrom">
                  対象月from
                </label>
                <input
                  id="bop030w020-historyMonthFrom"
                  type="month"
                  value={historyMonthFrom}
                  onChange={(e) => onHistoryMonthFromChange(e.target.value)}
                  onClick={handleDateInputClick}
                />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="bop030w020-historyMonthTo">
                  対象月to
                </label>
                <input
                  id="bop030w020-historyMonthTo"
                  type="month"
                  value={historyMonthTo}
                  onChange={(e) => onHistoryMonthToChange(e.target.value)}
                  onClick={handleDateInputClick}
                />
              </div>
            </form>
          </details>
        </div>

        <div className="action-panel history-action-panel">
          <div className="action-group action-group-right">
            <button
              form="historySearchForm"
              type="submit"
              className="search-button"
              disabled={isHistoryLoading}
            >
              検索
            </button>
          </div>
        </div>

        <div className="history-results">
          {isHistoryLoading && <Spinner label="履歴を取得中です…" />}
          {!isHistoryLoading && historyErrorMessage && (
            <div className="error-box">{historyErrorMessage}</div>
          )}
          {!isHistoryLoading && !historyErrorMessage && historyResults.length === 0 && (
            <div className="no-results-message">該当する履歴はありません</div>
          )}
          {!isHistoryLoading && !historyErrorMessage && historyResults.length > 0 && (
            <ul className="history-list">
              {historyResults.map((row) => (
                <li key={row.id} className="history-item">
                  <div className="history-item-header">
                    <span className="history-item-period">{formatHistoryPeriod(row)}</span>
                    <span className="history-item-badge">
                      {row.detailModeFlg === 1 ? "詳細データ分析" : "集計分析"}
                    </span>
                    <span className="history-item-model">{row.model}</span>
                    <span className="history-item-tokens">
                      {formatTokens(row.inputTokens, row.outputTokens)}
                    </span>
                    <span className="history-item-cost">{formatCostUsd(row.costUsd)}</span>
                    <span className="history-item-date">{formatHistoryDate(row.registerDt)}</span>
                  </div>
                  <div className="history-item-question">Q. {row.question}</div>
                  <div className="history-item-answer">{row.answer}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BOP030W020Template;
